/* eslint-disable no-console */
import React, {
    useCallback,
    useMemo,
    useRef,
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import { propTypes } from './props';
import { StudioWrapper } from './styled';
import { Editor, HelperSideBar, LoadingSpinner, Overlay, SideBar } from '@/components';
import {
    calculatePercentageValue,
    exportStageAsImage,
    isElementOfSameType,
    isElementShape,
    loadAllGoogleFonts,
    pickImage,
    preloadRelevantImages,
    removeImageProperty,
    validateLocale,
} from '@/utils';
import {
    CANVAS_SIZE_MODES,
    getCanvasSizeLabel,
    normalizeCanvasSize,
    pageSizes,
} from '@/constants/layoutConstants';
import {
    basicTextProps,
    defaultElements,
    defaultSideBarImagesList,
    elementTypes,
    sideBarpillsList,
} from '@/constants';
import theme from '@/theme';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { LOCATION_ELEMENT_ID, QRID_ELEMENT_ID } from '@/constants/textConstants';
/**
 * @typedef {Object} StudioProps
 * @property {string} [title]
 * @property {string} [defaultQrLogo]
 * @property {string} [languageLocale]
 * @property {Array} [elementsList]
 * @property {Array} [customImages]
 * @property {Array} [defaultImages]
 * @property {boolean} [disableWhiteLabel]
 * @property {Array} [defaultTemplatesList]
 * @property {Array} [customTemplatesList]
 * @property {'defaultFirst'|'customFirst'} [templatesDisplayOrder]
 * @property {boolean} [showOutsideBoundaryPreview]
 * @property {number} [outsideBoundaryPreviewPadding]
 * @property {string} [qrLink]
 * @property {Object} [styleProps]
 * @property {string} [defaultText]
 * @property {Function} onSave
 * @property {string} [saveButtonText]
 * @property {'en'|'ru'|'pl'|'de'|'es'|'fr'|'it'} [locale]
 * @property {Function} [onCreateNewTemplate] - called when user creates a new template, passes new canvas elements
 * @property {Function} [onTemplateSelect] - called when a template is selected, passes template id
 * @property {Function} [uploadQRLogoImage]
 *  - Optional callback to upload the QR logo image. Should return a string or an object with `{ url }`
 * @property {number} [zoomLevel] - Optional zoom level (e.g. 100 for 100%)
 * @property {boolean} [showBackgroundImagePicker] - Used to set background image on entire canvas
 * @property {boolean} [showOpacityPicker] - Related to showBackgroundImagePicker and it's opacity
 * @property {Array} [allowedFormats] - Allowed formats for image uploading functions
 * @property {boolean} showQrIdToggle (boolean, default false) → controls visibility of the QR ID toggle
 * @property {string | number} qrId (string | number | null, default null) → the value to render when toggled on
 * @property {boolean} [askDeleteConfirmation]
 * @property {{ mode: 'preset' | 'custom', preset?: string, widthMm?: number, heightMm?: number }} [canvasSize]
 * @property {Function} [onCanvasSizeChange]
 */

/**
 * @type {React.ForwardRefExoticComponent<StudioProps & React.RefAttributes<HTMLDivElement>>}
 */
export const Studio = forwardRef(
    (
        {
            title = '',
            defaultQrLogo = null,
            elementsList = defaultElements,
            uploadImageCallBack,
            customImages,
            defaultImages = defaultSideBarImagesList,
            disableWhiteLabel,
            defaultTemplatesList = [],
            customTemplatesList = [],
            templatesDisplayOrder = 'defaultFirst',
            showOutsideBoundaryPreview = false,
            outsideBoundaryPreviewPadding = 80,
            onDeleteCustomTemplate,
            qrLink = 'www.google.com',
            styleProps = {},
            defaultText,
            onSave,
            showSaveButton,
            saveButtonText,
            locale = 'en',
            onCreateNewTemplate,
            onTemplateSelect,
            showBackgroundImagePicker,
            showOpacityPicker,
            uploadQRLogoImage,
            zoomLevel,
            allowedFormats = ['png', 'jpg', 'jpeg', 'svg', 'webp'],
            showQrIdToggle = false,
            qrId = null,
            askDeleteConfirmation = true,
            canvasSize,
            onCanvasSizeChange,
        },
        ref,
    ) => {
        const stageRef = useRef(null);
        const elementCacheRef = useRef({});
        const qrIdActiveRef = useRef(false);
        const [copiedElement, setCopiedElement] = useState(null);
        const [selectedTab, setSelectedTab] = useState(sideBarpillsList.template);
        const [languageLocale, setLanguageLocale] = useState(validateLocale(locale));
        const [loadingFonts, setLoadingFonts] = useState(true);
        const [loadingImages, setLoadingImages] = useState(true);
        const [loading, setLoading] = useState(false);
        const [loadingUploadImage, setLoadingUploadImage] = useState(false);
        const [helperSideBarVisible, setHelperSideBarVisible] = useState(true);
        const [zoomPercentage, setZoomPercentage] = useState(zoomLevel ?? 100);
        const [elements, setElements] = useState(elementsList);
        const elementsRef = useRef(elements);
        const [backgroundImageOpacity, setBackgroundImageOpacity] = useState(1);
        const [history, setHistory] = useState([]);
        const [redoStack, setRedoStack] = useState([]);
        const [shapeFillColor, setShapeFillColor] = useState(theme.color.white);
        const [shapeStrokeWidth, setShapeStrokeWidth] = useState(1);
        const [shapeStrokeColor, setShapeStrokeColor] = useState(theme.color.black);
        const [shapeOpacity, setShapeOpacity] = useState(1);
        const [imageStrokeWidth, setImageStrokeWidth] = useState(0);
        const [imageStrokeColor, setImageStrokeColor] = useState(theme.color.black);
        const [imageOpacity, setImageOpacity] = useState(1);
        const [imageTintColor, setImageTintColor] = useState(theme.color.black);
        const [imageTintStrength, setImageTintStrength] = useState(0);
        const [qrStrokeWidth, setQrStrokeWidth] = useState(2);
        const [qrStrokeColor, setQrStrokeColor] = useState(theme.color.black);
        const [qrOpacity, setQrOpacity] = useState(1);
        const [selectedElement, setSelectedElement] = useState(null);
        const [customImagesList, setCustomImagesList] = useState(customImages || []);
        const [qrLogo, setQrLogo] = useState(defaultQrLogo);
        const [defaultTextProps, setDefaultTextProps] = useState(basicTextProps);
        const [currentQrId, setCurrentQrId] = useState(qrId);

        const overallLoading = loadingFonts || loadingImages || loadingUploadImage || loading;

        const getPageSizeElement = useCallback(
            (base = elements) => base?.find(e => e?.type === elementTypes?.pageSize),
            [elements],
        );

        const getNormalizedCanvasSize = useCallback(
            (base = elements, canvasSizeInput = canvasSize) => {
                if (canvasSizeInput) {
                    return normalizeCanvasSize(canvasSizeInput, pageSizes.A4);
                }
                const pageSizeElem = base?.find(e => e?.type === elementTypes?.pageSize);
                return normalizeCanvasSize(pageSizeElem, pageSizes.A4);
            },
            [elements, canvasSize],
        );

        const getPageDims = useCallback(
            (base = elements) => {
                const normalized = getNormalizedCanvasSize(base);
                return { width: normalized.widthPx, height: normalized.heightPx };
            },
            [elements, getNormalizedCanvasSize],
        );

        const findById = useCallback(
            (id, base = elements) => base.find(e => e.id === id),
            [elements],
        );

        /**
         * Compute the QR-ID frame:
         * - If LOCATION_ELEMENT_ID exists: center-align above it
         * - Otherwise: center of page
         */
        const computeQrIdFrame = useCallback(
            (base = elements) => {
                const { width: Cw, height: Ch } = getPageDims(base);
                const loc = findById(LOCATION_ELEMENT_ID, base);

                const fs = Math.max(
                    14,
                    Math.min(28, loc?.fontSize ?? defaultTextProps?.fontSize ?? 16),
                );
                const GAP = 8; // vertical gap above location text line
                const width = Number.isFinite(loc?.width) ? loc.width : 240;

                // center horizontally over location text (or page)
                const x = loc
                    ? Math.max(0, (loc.x ?? 0) + ((loc.width ?? width) - width) / 2)
                    : Math.max(0, (Cw - width) / 2);

                // place just above location text line (or vertical center)
                const y = loc ? Math.max(0, (loc.y ?? 0) - fs - GAP) : Math.max(0, (Ch - fs) / 2);

                const color = loc?.color ?? defaultTextProps?.color ?? '#111';
                const fontFamily = loc?.fontFamily ?? defaultTextProps?.fontFamily;

                return {
                    x,
                    y,
                    width,
                    textAlign: 'center',
                    fontSize: fs,
                    color,
                    fontFamily,
                };
            },
            [getPageDims, findById, defaultTextProps],
        );

        const undo = useCallback(() => {
            if (history.length > 0) {
                setSelectedElement(null);

                const previousState = history[history.length - 1];
                setRedoStack([elements, ...redoStack]);
                setHistory(history.slice(0, -1));
                setElements(previousState);
            }
        }, [history, elements, redoStack, setRedoStack, setHistory, setElements]);

        const redo = useCallback(() => {
            if (redoStack.length > 0) {
                setSelectedElement(null);

                const nextState = redoStack[0];
                setHistory([...history, elements]);
                setRedoStack(redoStack.slice(1));
                setElements(nextState);
            }
        }, [history, elements, redoStack, setHistory, setRedoStack, setElements]);

        useImperativeHandle(ref, () => ({
            onSave: async () => {
                setLoading(true);
                setSelectedElement(null);
                await new Promise(resolve => setTimeout(resolve, 100));
                let processedElements = removeImageProperty(elements);
                const dataURL = await exportStageAsImage(stageRef, null, 2, exportCropArea);
                setLoading(false);
                return { elements: processedElements, image: dataURL };
            },
            loading: overallLoading,
            setLoading,
            undoCount: history.length,
            hasChanges: history.length > 0,
            redoCount: redoStack.length,
            updateQrId: newId => {
                setCurrentQrId(newId);
                const idx = elements.findIndex(e => e.id === QRID_ELEMENT_ID);
                if (idx > -1) {
                    const frame = computeQrIdFrame(elements);
                    const newElements = [...elements];
                    newElements[idx] = { ...newElements[idx], text: String(newId), ...frame };
                    saveHistory(newElements);
                    if (selectedElement?.id === QRID_ELEMENT_ID)
                        setSelectedElement(newElements[idx]);
                }
            },
            reflowQrId: () => {
                const idx = elements.findIndex(e => e.id === QRID_ELEMENT_ID);
                if (idx === -1) return;
                const frame = computeQrIdFrame(elements);
                const copy = [...elements];
                copy[idx] = { ...copy[idx], ...frame };
                saveHistory(copy);
            },
        }));

        useEffect(() => {
            if (selectedElement && !elements.some(e => e.id === selectedElement.id)) {
                setSelectedElement(null);
            }
        }, [elements, selectedElement]);
        useEffect(() => {
            if (typeof zoomLevel === 'number') {
                setZoomPercentage(zoomLevel);
            }
        }, [zoomLevel]);

        useEffect(() => {
            const handleKeyDown = e => {
                const isMac = navigator.platform.toUpperCase().includes('MAC');
                const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
                const shiftKey = e.shiftKey;
                const activeElement = document.activeElement;
                const isTypingTarget =
                    ['INPUT', 'TEXTAREA'].includes(activeElement?.tagName) ||
                    activeElement?.isContentEditable;
                const isDeleteKey = e.key === 'Delete' || e.key === 'Del';
                const isBackspaceKey = e.key === 'Backspace';

                // Delete selected element with Delete (Win) or Backspace (Mac)
                if ((isDeleteKey || isBackspaceKey) && selectedElement && !isTypingTarget) {
                    e.preventDefault();
                    onDeleteSelectedElement();
                }

                // Copy
                if (ctrlOrCmd && e.key.toLowerCase() === 'c') {
                    if (selectedElement) {
                        e.preventDefault();
                        const copied = { ...selectedElement };
                        delete copied.ref;
                        setCopiedElement(copied);
                    }
                }

                // Paste
                if (ctrlOrCmd && e.key.toLowerCase() === 'v') {
                    if (copiedElement) {
                        e.preventDefault();
                        const newElement = {
                            ...copiedElement,
                            id: `element${Date.now()}`,
                            x: copiedElement.x + 10,
                            y: copiedElement.y + 10,
                            draggable: true,
                        };
                        addElement(newElement);
                        setSelectedElement(newElement);
                    }
                }

                // Undo: Ctrl/Cmd + Z
                if (ctrlOrCmd && !shiftKey && e.key.toLowerCase() === 'z') {
                    e.preventDefault();
                    undo();
                }

                // Redo: Ctrl/Cmd + Shift + Z or Ctrl + Y
                if (
                    (ctrlOrCmd && shiftKey && e.key.toLowerCase() === 'z') ||
                    (ctrlOrCmd && e.key.toLowerCase() === 'y')
                ) {
                    e.preventDefault();
                    redo();
                }
            };

            window.addEventListener('keydown', handleKeyDown);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
            };
        }, [selectedElement, copiedElement, undo, redo]);

        // console.log(isPropValid('onClick'));
        useEffect(() => {
            setLanguageLocale(validateLocale(locale));
        }, [locale]);

        useEffect(() => {
            setCustomImagesList(customImages || []);
        }, [customImages]);

        useEffect(() => {
            setLoadingFonts(true);
            loadAllGoogleFonts()
                .then(() => {
                    // console.log(msg);
                })
                .catch(err => {
                    console.error('❌ Error loading fonts:', err);
                })
                .finally(() => {
                    setTimeout(() => {
                        setLoadingFonts(false);
                    }, 1000);
                    console.log('🎉 Font loading complete!');
                });
        }, []);
        useEffect(() => {
            const incomingPageSizeElem = elementsList?.find(
                e => e?.type === elementTypes?.pageSize,
            );
            const nextCanvasSize = canvasSize
                ? normalizeCanvasSize(canvasSize, pageSizes.A4)
                : normalizeCanvasSize(incomingPageSizeElem, pageSizes.A4);
            const nextElements = elementsList.map(elem => {
                if (elem.type !== elementTypes.pageSize) return elem;
                const nextPageSize = { ...elem };

                if (nextCanvasSize.mode === CANVAS_SIZE_MODES.custom) {
                    delete nextPageSize.size;
                    nextPageSize.mode = CANVAS_SIZE_MODES.custom;
                    nextPageSize.widthMm = nextCanvasSize.widthMm;
                    nextPageSize.heightMm = nextCanvasSize.heightMm;
                } else {
                    delete nextPageSize.mode;
                    delete nextPageSize.widthMm;
                    delete nextPageSize.heightMm;
                    nextPageSize.size = nextCanvasSize.preset;
                }
                return nextPageSize;
            });
            if (!nextElements.some(elem => elem.type === elementTypes.pageSize)) {
                const injectedPageSize = {
                    type: elementTypes.pageSize,
                    id: `element${Date.now()}`,
                    size: nextCanvasSize.preset || pageSizes.A4,
                    cuttingGuideStroke: 0,
                    cuttingGuideStrokeColor: theme.color.black,
                };

                if (nextCanvasSize.mode === CANVAS_SIZE_MODES.custom) {
                    delete injectedPageSize.size;
                    injectedPageSize.mode = CANVAS_SIZE_MODES.custom;
                    injectedPageSize.widthMm = nextCanvasSize.widthMm;
                    injectedPageSize.heightMm = nextCanvasSize.heightMm;
                }

                nextElements.unshift(injectedPageSize);
            }

            LoadImages(nextElements);
            setElements(nextElements);
            const foundLoc = nextElements?.find(
                e => e.id === LOCATION_ELEMENT_ID && e.type === elementTypes.text,
            );
            if (foundLoc) {
                // store a clean copy (no runtime refs)
                const { ...rest } = foundLoc || {};
                elementCacheRef.current[LOCATION_ELEMENT_ID] = { ...rest };
            }
        }, [canvasSize, ...elementsList]);

        const LoadImages = elems => {
            setLoadingImages(true);
            preloadRelevantImages(elems)
                .then(({ errors }) => {
                    // console.log('✅ Loaded images:', successes);
                    if (errors?.length > 0) {
                        console.log('❌ Failed images:', errors);
                    }
                })
                .catch(err => {
                    console.error('Something went really wrong:', err);
                })
                .finally(() => {
                    setLoadingImages(false);
                });
        };

        const editorHeight = useMemo(() => {
            const { height } = getPageDims(elements);
            return calculatePercentageValue(height, zoomPercentage);
        }, [zoomPercentage, elements, getPageDims]);
        const editorWidth = useMemo(() => {
            const { width } = getPageDims(elements);
            return calculatePercentageValue(width, zoomPercentage);
        }, [zoomPercentage, elements, getPageDims]);
        const normalizedOutsidePreviewPadding = useMemo(
            () => Math.max(0, Number(outsideBoundaryPreviewPadding) || 0),
            [outsideBoundaryPreviewPadding],
        );
        const exportCropArea = useMemo(() => {
            if (!showOutsideBoundaryPreview) return null;

            return {
                x: normalizedOutsidePreviewPadding,
                y: normalizedOutsidePreviewPadding,
                width: editorWidth,
                height: editorHeight,
            };
        }, [
            showOutsideBoundaryPreview,
            normalizedOutsidePreviewPadding,
            editorWidth,
            editorHeight,
        ]);
        const selectedPageSize = useMemo(() => {
            return getNormalizedCanvasSize(elements);
        }, [elements, getNormalizedCanvasSize]);

        const selectedPageSizeLabel = useMemo(
            () => getCanvasSizeLabel(selectedPageSize),
            [selectedPageSize],
        );

        const hasLocationText = useMemo(
            () => elements.some(e => e.type === elementTypes.text && e.id === LOCATION_ELEMENT_ID),
            [elements],
        );
        const hasQrIdText = useMemo(
            () => elements.some(e => e.type === elementTypes.text && e.id === QRID_ELEMENT_ID),
            [elements],
        );

        useEffect(() => {
            qrIdActiveRef.current = hasQrIdText; // keep this in sync
        }, [hasQrIdText]);

        const removeElementById = id => {
            const idx = elements.findIndex(e => e.id === id);
            if (idx > -1) {
                const snapshot = { ...elements[idx] };
                delete snapshot.ref;
                // cache latest props before removing
                if (id !== QRID_ELEMENT_ID) {
                    elementCacheRef.current[id] = snapshot; // never cache QR-ID
                }
                const next = elements.slice();
                next.splice(idx, 1);
                if (selectedElement?.id === id) setSelectedElement(null);
                saveHistory(next);
            }
        };

        const addElementFromCache = (id, fallbackFactory) => {
            const cached = elementCacheRef.current[id];
            const toAdd = cached ? { ...cached, id } : fallbackFactory?.(id);
            if (!toAdd) return; // nothing to add (no cache + no fallback)
            // Make sure required fields are sane
            if (!toAdd.type) toAdd.type = elementTypes.text;
            if (typeof toAdd.draggable !== 'boolean') toAdd.draggable = true;
            saveHistory([...elements, toAdd]);
            setSelectedElement(toAdd);
        };

        const addQrIdText = (base = elements) => {
            if (!currentQrId || base.some(e => e.id === QRID_ELEMENT_ID)) return;

            const frame = computeQrIdFrame(base);

            const textProps = {
                id: QRID_ELEMENT_ID,
                type: elementTypes.text,
                draggable: true,
                text: String(currentQrId),
                fontWeight: '700', // always bold
                textAlign: 'center',
                opacity: defaultTextProps?.opacity,
                ...frame,
            };

            const next = [...base, textProps];
            saveHistory(next);
            setSelectedElement(textProps);
        };

        const toggleLocationText = () => {
            if (hasLocationText) {
                removeElementById(LOCATION_ELEMENT_ID);
            } else {
                // If first-time and no cache, create a sensible fallback
                addElementFromCache(LOCATION_ELEMENT_ID, id => {
                    // fallback only used if the element was never seen before
                    return {
                        id,
                        type: elementTypes.text,
                        draggable: true,
                        // a neutral default; you likely won’t hit this after first toggle
                        x: 24,
                        y: 24,
                        text: 'Property location',
                        opacity: defaultTextProps?.opacity,
                        fontSize: defaultTextProps?.fontSize,
                        fontFamily: defaultTextProps?.fontFamily,
                        color: defaultTextProps?.color,
                        textDecoration: defaultTextProps?.textDecoration,
                        fontStyle: defaultTextProps?.fontStyle,
                        fontWeight: defaultTextProps?.fontWeight,
                        textAlign: defaultTextProps?.textAlign,
                    };
                });
            }
        };

        const toggleQrIdText = () => {
            hasQrIdText ? removeElementById(QRID_ELEMENT_ID) : addQrIdText();
        };

        const onSelectedTab = useCallback(
            tab => {
                setSelectedElement(null);
                setSelectedTab(tab);
                setHelperSideBarVisible(true);
            },
            [setSelectedTab],
        );

        const onCollapseHelperSideBar = useCallback(() => {
            setHelperSideBarVisible(false);
        }, [setHelperSideBarVisible]);

        const onChangeZoomPercentage = percentage => {
            setZoomPercentage(percentage);
        };

        const saveHistory = newElements => {
            setHistory([...history, elements]);
            setRedoStack([]);
            setElements(newElements);
        };

        const onSetBackgroundColor = color => {
            if (!color) {
                return;
            }
            let newElement;
            let elemClone = [...elements];
            let backgroundColorIndex = elemClone.findIndex(
                e => e?.type === elementTypes?.backgroundColor,
            );
            if (backgroundColorIndex > -1) {
                elemClone.splice(backgroundColorIndex, 1);
            }
            newElement = {
                type: elementTypes?.backgroundColor,
                id: `element${Date.now()}`,
                color: color,
            };

            saveHistory([...elemClone, newElement]);
        };
        const onSetPageSize = newSize => {
            if (!newSize) return;

            const oldDims = getNormalizedCanvasSize(elements);
            const newDims = normalizeCanvasSize(newSize, pageSizes.A4);
            const scaleX = newDims.widthPx / oldDims.widthPx;
            const scaleY = newDims.heightPx / oldDims.heightPx;

            const pageSizeElem = getPageSizeElement(elements);
            const pageSizeId = pageSizeElem?.id || `element${Date.now()}`;

            const scaledElements = elements.map(elem => {
                if (elem.type === elementTypes.pageSize) {
                    const nextPageElem = {
                        ...elem,
                        id: pageSizeId,
                    };

                    if (newDims.mode === CANVAS_SIZE_MODES.custom) {
                        delete nextPageElem.size;
                        nextPageElem.mode = CANVAS_SIZE_MODES.custom;
                        nextPageElem.widthMm = newDims.widthMm;
                        nextPageElem.heightMm = newDims.heightMm;
                    } else {
                        delete nextPageElem.mode;
                        delete nextPageElem.widthMm;
                        delete nextPageElem.heightMm;
                        nextPageElem.size = newDims.preset;
                    }
                    return nextPageElem;
                }

                let scaled = { ...elem };

                // Scale common position and size props
                if (typeof scaled.x === 'number') scaled.x *= scaleX;
                if (typeof scaled.y === 'number') scaled.y *= scaleY;
                if (typeof scaled.width === 'number') scaled.width *= scaleX;
                if (typeof scaled.height === 'number') scaled.height *= scaleY;
                if (typeof scaled.radius === 'number') scaled.radius *= Math.min(scaleX, scaleY); // for circles, stars
                if (typeof scaled.fontSize === 'number')
                    scaled.fontSize *= Math.min(scaleX, scaleY);

                // Special case: QR codes or icons that have scale or size props
                if (scaled.points && Array.isArray(scaled.points)) {
                    scaled.points = scaled.points.map((val, idx) =>
                        idx % 2 === 0 ? val * scaleX : val * scaleY,
                    );
                }

                return scaled;
            });

            if (!scaledElements.some(elem => elem.type === elementTypes.pageSize)) {
                const newPageElement = {
                    type: elementTypes.pageSize,
                    id: pageSizeId,
                    size: newDims.preset || pageSizes.A4,
                    cuttingGuideStroke: 0,
                    cuttingGuideStrokeColor: theme.color.black,
                };

                if (newDims.mode === CANVAS_SIZE_MODES.custom) {
                    delete newPageElement.size;
                    newPageElement.mode = CANVAS_SIZE_MODES.custom;
                    newPageElement.widthMm = newDims.widthMm;
                    newPageElement.heightMm = newDims.heightMm;
                }
                scaledElements.unshift(newPageElement);
            }

            saveHistory(removeImageProperty(scaledElements));

            if (typeof onCanvasSizeChange === 'function') {
                if (newDims.mode === CANVAS_SIZE_MODES.custom) {
                    onCanvasSizeChange({
                        mode: CANVAS_SIZE_MODES.custom,
                        widthMm: newDims.widthMm,
                        heightMm: newDims.heightMm,
                    });
                } else {
                    onCanvasSizeChange({
                        mode: CANVAS_SIZE_MODES.preset,
                        preset: newDims.preset,
                    });
                }
            }
        };

        const onChangeCuttingGuideProp = (type, value) => {
            let elemClone = JSON.parse(JSON.stringify(elements));
            let sizeIndex = elemClone.findIndex(e => e?.type === elementTypes?.pageSize);
            if (sizeIndex > -1) {
                elemClone[sizeIndex][type] = value;
            } else {
                let sizeElement = {
                    type: elementTypes?.pageSize,
                    id: `element${Date.now()}`,
                };
                sizeElement[type] = value;
                elemClone.push(sizeElement);
            }
            saveHistory(removeImageProperty(elemClone));
        };

        const addElement = elem => {
            saveHistory([...elements, elem]);
            setSelectedElement(elem);
        };

        const addShape = type => {
            let shapeProps = {
                id: `element${Date.now()}`,
                x: editorWidth / 2 - 50,
                y: editorHeight / 2 - 50,
                fill: shapeFillColor,
                stroke: shapeStrokeColor,
                strokeWidth: shapeStrokeWidth,
                opacity: shapeOpacity,
                type: type,
                draggable: true,
            };
            if (type === elementTypes.circle) {
                shapeProps['radius'] = 70;
                shapeProps['x'] = editorWidth / 2;
                shapeProps['y'] = editorHeight / 2;
                addElement(shapeProps);
            } else if (type === elementTypes.square) {
                shapeProps['height'] = 100;
                shapeProps['width'] = 100;
                shapeProps['name'] = elementTypes.square;
                shapeProps['cornerRadius'] = 0;
                addElement(shapeProps);
            } else if (type === elementTypes.arrowRight) {
                shapeProps['points'] = [0, 0, 100, 0];
                shapeProps['pointerLength'] = 20;
                shapeProps['pointerWidth'] = 20;
                shapeProps['strokeWidth'] = 4;
                shapeProps['fill'] = shapeStrokeColor;
                addElement(shapeProps);
            } else if (type === elementTypes.triangle) {
                shapeProps['sides'] = 3;
                shapeProps['radius'] = 60;
                shapeProps['rotation'] = 0;
                shapeProps['x'] = editorWidth / 2;
                shapeProps['y'] = editorHeight / 2;
                addElement(shapeProps);
            } else if (type === elementTypes.heart) {
                shapeProps['data'] = `M 50,20 
            C 20,-10 -30,40 50,90 
            C 130,40 80,-10 50,20 Z`;
                shapeProps['scale'] = { x: 1, y: 1 };
                addElement(shapeProps);
            } else if (type === elementTypes.star) {
                shapeProps['numPoints'] = 5;
                shapeProps['innerRadius'] = 30;
                shapeProps['outerRadius'] = 70;
                shapeProps['x'] = editorWidth / 2;
                shapeProps['y'] = editorHeight / 2;
                addElement(shapeProps);
            } else if (type === elementTypes.horizontalLine) {
                const centerY = editorHeight / 2;
                shapeProps['points'] = [50, centerY, editorWidth - 50, centerY];
                shapeProps['x'] = 0;
                shapeProps['y'] = 0;
                shapeProps['strokeWidth'] = 4;

                addElement(shapeProps);
            } else if (type === elementTypes.verticalLine) {
                const centerX = editorWidth / 2;
                shapeProps['points'] = [centerX, 50, centerX, editorHeight - 50];
                shapeProps['x'] = 0;
                shapeProps['y'] = 0;
                shapeProps['strokeWidth'] = 4;

                addElement(shapeProps);
            }
            // console.error(type);
        };

        const onSetBackgroundImage = img => {
            let newElement;
            let elemClone = [...elements];
            let backgroundImageIndex = elemClone.findIndex(
                e => e?.type === elementTypes?.backgroundImage,
            );
            if (backgroundImageIndex > -1) {
                elemClone.splice(backgroundImageIndex, 1);
            }
            newElement = {
                type: elementTypes?.backgroundImage,
                id: `element${Date.now()}`,
                src: img,
                opacity: backgroundImageOpacity,
            };
            saveHistory([...elemClone, newElement]);
        };
        const onRemoveBackgroundImage = () => {
            let elemClone = JSON.parse(JSON.stringify(elements));
            let backgroundImageIndex = elemClone.findIndex(
                e => e?.type === elementTypes?.backgroundImage,
            );
            if (backgroundImageIndex > -1) {
                elemClone.splice(backgroundImageIndex, 1);
                saveHistory(elemClone);
            }
        };

        const onChangeBackgroundImageOpacity = val => {
            let backgroundImageIndex = elements?.findIndex(
                e => e?.type === elementTypes?.backgroundImage,
            );
            if (backgroundImageIndex > -1) {
                const updatedElements = elements?.map(el =>
                    el.type === elementTypes?.backgroundImage ? { ...el, opacity: val } : el,
                );

                saveHistory(updatedElements);
            }
            setBackgroundImageOpacity(val);
        };

        const onSelectElement = element => {
            if (isElementShape(element)) {
                setSelectedTab(sideBarpillsList?.shape);
            } else if (isElementOfSameType(element, elementTypes?.image)) {
                setSelectedTab(sideBarpillsList?.image);
            } else if (isElementOfSameType(element, elementTypes?.qr)) {
                setSelectedTab(sideBarpillsList?.qr);
            } else if (isElementOfSameType(element, elementTypes?.text)) {
                setSelectedTab(sideBarpillsList?.text);
            }
            if (element) {
                let elem = element;
                delete elem['ref'];
                setSelectedElement({ ...elem });
                setHelperSideBarVisible(true);
            } else {
                setSelectedElement(null);
            }
        };

        const changeSelectedElementProperty = (key, value) => {
            let selectedElementCopy = { ...selectedElement };
            selectedElementCopy[key] = value;
            delete selectedElementCopy['ref'];
            const updatedElements = elements?.map(el =>
                el.id === selectedElementCopy?.id ? { ...selectedElementCopy } : el,
            );
            saveHistory(updatedElements);
            setSelectedElement(selectedElementCopy);
        };

        const onChangeShapeFill = e => {
            if (selectedElement && isElementShape(selectedElement)) {
                changeSelectedElementProperty('fill', e);
            } else {
                setShapeFillColor(e);
            }
        };

        const onChangeShapeStrokeWidth = e => {
            if (selectedElement && isElementShape(selectedElement)) {
                changeSelectedElementProperty('strokeWidth', e);
            } else {
                setShapeStrokeWidth(e);
            }
        };

        const onChangeShapeStrokeColor = e => {
            if (selectedElement && isElementShape(selectedElement)) {
                changeSelectedElementProperty('stroke', e);
            } else {
                setShapeStrokeColor(e);
            }
        };

        const onChangeShapeOpacity = e => {
            if (selectedElement && isElementShape(selectedElement)) {
                changeSelectedElementProperty('opacity', e);
            } else {
                setShapeOpacity(e);
            }
        };
        const onDeleteSelectedElement = () => {
            let index = elements?.findIndex(e => e?.id === selectedElement?.id);
            if (index < 0) {
                return;
            }
            let copy = [...elements];
            copy.splice(index, 1);
            setSelectedElement(null);
            saveHistory([...copy]);
        };
        const onCopySelectedElement = () => {
            let copyElement = { ...selectedElement };
            copyElement['id'] = `element${Date.now()}`;
            copyElement['x'] = selectedElement?.x + 10;
            copyElement['y'] = selectedElement?.y + 10;
            copyElement['draggable'] = true;

            addElement(copyElement);
        };
        const onToggleLockElement = () => {
            changeSelectedElementProperty('draggable', !selectedElement?.draggable);
            // setSelectedElement(null);
        };
        const bringSelectedElementToFront = () => {
            let copyElements = [...elements];
            const index = copyElements?.findIndex(elem => elem.id === selectedElement?.id);
            if (index === -1) {
                return;
            }
            const [selectedElem] = copyElements?.splice(index, 1);
            copyElements?.push(selectedElem);
            saveHistory([...copyElements]);
        };
        const createNewTemplate = (
            elems = [
                ...defaultElements,
                {
                    type: elementTypes.qr,
                    id: `element${Date.now() + 1}`,
                    width: 110,
                    height: 110,
                    x: 194.5,
                    y: 280,
                    fill: theme.color.green_80D965,
                    stroke: theme.color.black,
                    strokeWidth: 2,
                    draggable: true,
                    qrLogo: null,
                    qrText: qrLink,
                    opacity: 1,
                    logoVisible: false,
                },
            ],
        ) => {
            const prevHadQrId = elementsRef.current?.some(e => e.id === QRID_ELEMENT_ID);
            let next = [...elems];

            // If toggle was ON before and current template doesn’t include QR-ID, re-add it above Location (or center)
            if (
                prevHadQrId &&
                !next.some(e => e.id === QRID_ELEMENT_ID) &&
                showQrIdToggle &&
                currentQrId
            ) {
                const frame = computeQrIdFrame(next);
                next.push({
                    id: QRID_ELEMENT_ID,
                    type: elementTypes.text,
                    draggable: true,
                    text: String(currentQrId),
                    fontWeight: '700',
                    textAlign: 'center',
                    opacity: defaultTextProps?.opacity,
                    ...frame,
                });
            }

            setElements([...elems]);
            setHistory([]);
            setRedoStack([]);

            if (typeof onCreateNewTemplate === 'function') {
                onCreateNewTemplate(elems);
            }

            // Clear selected template ID
            if (typeof onTemplateSelect === 'function') {
                onTemplateSelect(null);
            }
        };

        useEffect(() => {
            elementsRef.current = elements;
        }, [elements]);

        const sendSelectedElementToBack = () => {
            let copyElements = [...elements];
            const index = copyElements?.findIndex(elem => elem.id === selectedElement?.id);
            if (index === -1) {
                return;
            }
            const [selectedElem] = copyElements?.splice(index, 1);
            copyElements?.unshift(selectedElem);
            saveHistory([...copyElements]);
        };
        const onChangeImageStrokeWidth = e => {
            if (isElementOfSameType(selectedElement, elementTypes?.image)) {
                changeSelectedElementProperty('strokeWidth', e);
            } else {
                setImageStrokeWidth(e);
            }
        };

        const onChangeImageStrokeColor = e => {
            if (isElementOfSameType(selectedElement, elementTypes?.image)) {
                changeSelectedElementProperty('stroke', e);
            } else {
                setImageStrokeColor(e);
            }
        };

        const onChangeImageOpacity = e => {
            if (isElementOfSameType(selectedElement, elementTypes?.image)) {
                changeSelectedElementProperty('opacity', e);
            } else {
                setImageOpacity(e);
            }
        };

        const onChangeImageTintColor = color => {
            if (isElementOfSameType(selectedElement, elementTypes?.image)) {
                changeSelectedElementProperty('tintColor', color);
            }
            setImageTintColor(color);
        };

        const onChangeImageTintStrength = value => {
            const nextStrength = Math.max(0, Math.min(1, value));
            if (isElementOfSameType(selectedElement, elementTypes?.image)) {
                changeSelectedElementProperty('tintStrength', nextStrength);
            } else {
                setImageTintStrength(nextStrength);
            }
        };

        const onClearImageTint = () => {
            if (isElementOfSameType(selectedElement, elementTypes?.image)) {
                changeSelectedElementProperty('tintStrength', 0);
            }
            setImageTintStrength(0);
        };

        const addCustomImageToList = img => {
            // console.error({img});
            setCustomImagesList([...customImagesList, img]);
        };

        const addImageToCanvas = img => {
            let imgProps = {
                id: `element${Date.now()}`,
                x: editorWidth / 2 - 50,
                y: editorHeight / 2 - 50,
                stroke: imageStrokeColor,
                strokeWidth: imageStrokeWidth,
                opacity: imageOpacity,
                tintColor: imageTintColor,
                tintStrength: imageTintStrength,
                type: elementTypes.image,
                draggable: true,
                // width: 100,
                // height: 100,
                src: img,
            };
            addElement(imgProps);
        };
        const addTextToCanvas = e => {
            let text = e ? e : defaultTextProps;
            let textProps = {
                id: `element${Date.now()}`,
                x: editorWidth / 2 - 100,
                y: editorHeight / 2,
                opacity: text?.opacity || defaultTextProps?.opacity,
                type: elementTypes.text,
                draggable: true,
                text: text?.text || defaultTextProps?.text,
                fontSize: text?.fontSize || defaultTextProps?.fontSize,
                fontFamily: text?.fontFamily || defaultTextProps?.fontFamily,
                color: text?.color || defaultTextProps?.color,
                textDecoration: text?.textDecoration || defaultTextProps?.textDecoration,
                fontStyle: text?.fontStyle || defaultTextProps?.fontStyle,
                fontWeight: text?.fontWeight || defaultTextProps?.fontWeight,
                textAlign: text?.textAlign || defaultTextProps?.textAlign,
            };
            if (text?.width) {
                textProps['width'] = text?.width;
            }

            addElement(textProps);
        };

        const addQrCode = () => {
            let qrProps = {
                id: `element${Date.now()}`,
                type: elementTypes.qr,
                height: 110,
                width: 110,
                x: editorWidth / 2 - 50,
                y: editorHeight / 2 - 50,
                fill: theme.color.green_80D965,
                stroke: qrStrokeColor,
                strokeWidth: qrStrokeWidth,
                draggable: true,
                qrLogo: null,
                qrText: qrLink,
                opacity: qrOpacity,
                logoVisible: false,
            };
            addElement(qrProps);
        };

        const removeQrCode = () => {
            let copy = elements?.filter(el => el.type !== elementTypes?.qr);
            if (selectedElement?.type === elementTypes?.qr) {
                setSelectedElement(null);
            }
            saveHistory([...copy]);
        };
        const onChangeQrStrokeWidth = e => {
            if (isElementOfSameType(selectedElement, elementTypes?.qr)) {
                changeSelectedElementProperty('strokeWidth', e);
            } else {
                setQrStrokeWidth(e);
            }
        };

        const onChangeQrStrokeColor = e => {
            if (isElementOfSameType(selectedElement, elementTypes?.qr)) {
                changeSelectedElementProperty('stroke', e);
            } else {
                setQrStrokeColor(e);
            }
        };

        const onChangeQrOpacity = e => {
            if (isElementOfSameType(selectedElement, elementTypes?.qr)) {
                changeSelectedElementProperty('opacity', e);
            } else {
                setQrOpacity(e);
            }
        };
        const toggleQr = () => {
            if (elements?.findIndex(elem => elem.type === elementTypes?.qr) > -1) {
                removeQrCode();
            } else {
                addQrCode();
            }
        };
        const turnLogoVisibiltyOn = () => {
            const updatedElements = elements?.map(el =>
                el.type === elementTypes?.qr
                    ? { ...el, logoVisible: true, qrLogo: el?.qrLogo || qrLogo }
                    : el,
            );
            saveHistory(updatedElements);
        };
        const turnLogoVisibiltyOff = () => {
            const updatedElements = elements?.map(el =>
                el.type === elementTypes?.qr ? { ...el, logoVisible: false } : el,
            );
            saveHistory(updatedElements);
        };
        const toggleQrLogo = () => {
            let index = elements?.findIndex(elem => elem.type === elementTypes?.qr);
            if (index > -1) {
                setSelectedElement(null);
                if (elements?.[index]?.logoVisible) {
                    turnLogoVisibiltyOff();
                } else {
                    turnLogoVisibiltyOn();
                }
            }
        };

        const addQrLogo = async () => {
            const file = await pickImage();
            if (!file) return;

            if (uploadQRLogoImage) {
                try {
                    setLoadingUploadImage(true);
                    const result = await uploadQRLogoImage(file);

                    const imageUrl = typeof result === 'string' ? result : result?.url;

                    if (imageUrl) {
                        addLogo(imageUrl);
                    } else {
                        console.warn('uploadQRLogoImage did not return a valid image URL');
                    }
                } catch (error) {
                    console.error('Error uploading QR logo image:', error);
                } finally {
                    setLoadingUploadImage(false);
                }
            } else {
                const reader = new FileReader();
                reader.onload = () => {
                    addLogo(reader.result);
                };
                reader.readAsDataURL(file);
            }
        };
        const addLogo = url => {
            let index = elements?.findIndex(elem => elem.type === elementTypes?.qr);
            setQrLogo(url);
            if (index > -1) {
                // if (elements[index]?.logoVisible) {
                const updatedElements = elements?.map(el =>
                    el.type === elementTypes?.qr ? { ...el, qrLogo: url } : el,
                );
                saveHistory(updatedElements);
                // }
            }
        };

        const onChangeTextProperty = (key, value) => {
            if (isElementOfSameType(selectedElement, elementTypes?.text)) {
                changeSelectedElementProperty(key, value);
            } else {
                let copyDefultProps = { ...defaultTextProps };
                copyDefultProps[key] = value;
                setDefaultTextProps({ ...copyDefultProps });
            }
        };
        const onSaveProgress = async () => {
            if (!onSave) {
                return;
            }
            setLoading(true);
            setSelectedElement(null);
            await new Promise(resolve => setTimeout(resolve, 100));
            let processedElements = removeImageProperty(elements);
            const dataURL = await exportStageAsImage(stageRef, null, 2, exportCropArea);
            setLoading(false);
            onSave({ elements: processedElements, image: dataURL });
        };

        return (
            <StyleSheetManager shouldForwardProp={isPropValid}>
                <StudioWrapper>
                    <SideBar
                        selectedSideBarItem={selectedTab}
                        onClickPill={onSelectedTab}
                        languageLocale={languageLocale}
                        disableWhiteLabel={disableWhiteLabel}
                        styleProps={styleProps}
                    />
                    <HelperSideBar
                        selectedSideBarItem={selectedTab}
                        onCollapse={onCollapseHelperSideBar}
                        helperSideBarVisible={helperSideBarVisible}
                        onAddShape={addShape}
                        defaultImagesList={defaultImages}
                        customImagesList={customImagesList}
                        onAddCustomImageToList={addCustomImageToList}
                        onAddImageToCanvas={addImageToCanvas}
                        onAddTextToCanvas={addTextToCanvas}
                        toggleQr={toggleQr}
                        qrPresent={elements?.findIndex(elem => elem.type === elementTypes?.qr) > -1}
                        toggleQrLogo={toggleQrLogo}
                        addQrLogo={addQrLogo}
                        qrLogo={
                            elements?.[elements?.findIndex(elem => elem.type === elementTypes?.qr)]
                                ?.qrLogo || qrLogo
                        }
                        elements={elements}
                        oncreateNewTemplate={createNewTemplate}
                        uploadImageCallBack={uploadImageCallBack}
                        setLoadingUploadImage={setLoadingUploadImage}
                        defaultTemplatesList={defaultTemplatesList}
                        customTemplatesList={customTemplatesList}
                        templatesDisplayOrder={templatesDisplayOrder}
                        onDeleteCustomTemplate={onDeleteCustomTemplate}
                        askDeleteConfirmation={askDeleteConfirmation}
                        styleProps={styleProps}
                        defaultText={defaultText}
                        languageLocale={languageLocale}
                        onTemplateSelect={onTemplateSelect}
                        allowedFormats={allowedFormats}
                        isLocationTextActive={hasLocationText}
                        onToggleLocationText={toggleLocationText}
                        showQrIdToggle={!!showQrIdToggle}
                        isQrIdTextActive={hasQrIdText}
                        onToggleQrIdText={toggleQrIdText}
                    />
                    {loadingFonts ? null : (
                        <Editor
                            setLoading={setLoading}
                            cuttingGuideStroke={
                                elements?.find(e => e?.type === elementTypes?.pageSize)
                                    ?.cuttingGuideStroke || 0
                            }
                            cuttingGuideStrokeColor={
                                elements?.find(e => e?.type === elementTypes?.pageSize)
                                    ?.cuttingGuideStrokeColor || theme?.color.black
                            }
                            onChangeCuttingGuideProp={(type, value) => {
                                onChangeCuttingGuideProp(type, value);
                            }}
                            editorHeight={editorHeight}
                            editorWidth={editorWidth}
                            selectedTab={selectedTab}
                            title={title}
                            zoomPercentage={zoomPercentage}
                            onChangeZoomPercentage={onChangeZoomPercentage}
                            onSetBackgroundColor={onSetBackgroundColor}
                            elements={elements}
                            history={history}
                            redoStack={redoStack}
                            onSetBackgroundImage={onSetBackgroundImage}
                            onUndo={undo}
                            onRedo={redo}
                            shapeStrokeColor={shapeStrokeColor}
                            shapeStrokeWidth={shapeStrokeWidth}
                            shapeFillColor={shapeFillColor}
                            imageStrokeWidth={imageStrokeWidth}
                            imageStrokeColor={imageStrokeColor}
                            imageOpacity={imageOpacity}
                            imageTintColor={imageTintColor}
                            imageTintStrength={imageTintStrength}
                            onChangeImageStrokeWidth={onChangeImageStrokeWidth}
                            onChangeImageStrokeColor={onChangeImageStrokeColor}
                            onChangeImageOpacity={onChangeImageOpacity}
                            onChangeImageTintColor={onChangeImageTintColor}
                            onChangeImageTintStrength={onChangeImageTintStrength}
                            onClearImageTint={onClearImageTint}
                            onChangeShapeStrokeColor={onChangeShapeStrokeColor}
                            onChangeShapeStrokeWidth={onChangeShapeStrokeWidth}
                            onChangeShapeFill={onChangeShapeFill}
                            shapeOpacity={shapeOpacity}
                            onChangeShapeOpacity={onChangeShapeOpacity}
                            qrStrokeWidth={qrStrokeWidth}
                            qrStrokeColor={qrStrokeColor}
                            qrOpacity={qrOpacity}
                            onChangeQrStrokeWidth={onChangeQrStrokeWidth}
                            onChangeQrStrokeColor={onChangeQrStrokeColor}
                            onChangeQrOpacity={onChangeQrOpacity}
                            selectedElement={selectedElement}
                            onSelectElement={onSelectElement}
                            stageRef={stageRef}
                            saveHistory={saveHistory}
                            onChangeBackgroundImageOpacity={onChangeBackgroundImageOpacity}
                            backgroundImageOpacity={
                                elements?.find(e => e?.type === elementTypes?.backgroundImage)
                                    ?.opacity || backgroundImageOpacity
                            }
                            onDeleteSelectedElement={onDeleteSelectedElement}
                            onCopySelectedElement={onCopySelectedElement}
                            onToggleLockElement={onToggleLockElement}
                            bringSelectedElementToFront={bringSelectedElementToFront}
                            sendSelectedElementToBack={sendSelectedElementToBack}
                            defaultTextProps={defaultTextProps}
                            onChangeTextProperty={onChangeTextProperty}
                            onSetPageSize={onSetPageSize}
                            selectedPageSize={selectedPageSize}
                            selectedPageSizeLabel={selectedPageSizeLabel}
                            loadingImages={loadingImages}
                            loadingFonts={loadingFonts}
                            uploadImageCallBack={uploadImageCallBack}
                            setLoadingUploadImage={setLoadingUploadImage}
                            onSave={onSave && onSaveProgress}
                            showSaveButton={showSaveButton}
                            onSetSelectedTab={onSelectedTab}
                            saveButtonText={saveButtonText}
                            languageLocale={languageLocale}
                            onRemoveBackgroundImage={onRemoveBackgroundImage}
                            showBackgroundImagePicker={showBackgroundImagePicker}
                            showOpacityPicker={showOpacityPicker}
                            changeSelectedElementProperty={changeSelectedElementProperty}
                            showOutsideBoundaryPreview={showOutsideBoundaryPreview}
                            outsideBoundaryPreviewPadding={normalizedOutsidePreviewPadding}
                        />
                    )}
                    {overallLoading && (
                        <Overlay>
                            <LoadingSpinner size="60px" color={theme.color.secondary} />
                        </Overlay>
                    )}
                </StudioWrapper>
            </StyleSheetManager>
        );
    },
);

Studio.propTypes = propTypes;
Studio.displayName = 'Studio';
